import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from '../firebase';
import { getUserProfile, updateUserProfile } from '../services/userService';
import {
    Typography,
    Container,
    Card,
    CardContent,
    Box,
    Alert,
} from '@mui/material';
import UserProfileForm from './UserProfileForm';
import { User } from '../types/shared';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';

const Settings: React.FC = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState<FirebaseUser | null>(null);
    const [profile, setProfile] = useState<Partial<User>>({
        location: '',
        foodPreferences: '',
        availability: '',
        language: i18n.language || 'en'
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (u) => {
            if (u) {
                setUser(u);
                try {
                    const userProfile = await getUserProfile(u.uid);
                    // safe cast as UserProfile from service matches our shared User partials structurally for these fields
                    const mergedProfile: Partial<User> = {
                        location: '',
                        foodPreferences: '',
                        availability: '',
                        language: 'en',
                        ...userProfile
                    } as unknown as Partial<User>;
                    setProfile(mergedProfile);
                } catch (error) {
                    console.error("Error fetching profile", error);
                }
            } else {
                navigate('/login');
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [navigate]);

    const handleSave = async (profileData: any) => {
        if (user) {
            try {
                await updateUserProfile(user.uid, profileData);
                setMessage(t('settings.save_success'));
                setTimeout(() => setMessage(''), 3000);
                // Update local state to reflect saved changes
                setProfile(prev => ({ ...prev, ...profileData }));
            } catch (error) {
                console.error("Error saving settings:", error);
                setMessage(t('settings.save_failed'));
            }
        }
    };

    if (loading) return <Typography>{t('settings.loading')}</Typography>;

    return (
        <Box sx={{ py: 4 }}>
            <Container maxWidth="sm">
                <Typography variant="h4" gutterBottom sx={{ color: 'primary.main' }}>
                    {t('settings.settings_title')}
                </Typography>
                <Card>
                    <CardContent>
                        {message && <Alert severity={message.includes('Failed') ? 'error' : 'success'} sx={{ mb: 2 }}>{message}</Alert>}

                        <UserProfileForm
                            initialValues={profile}
                            onSave={handleSave}
                            saveLabel={t('settings.save_btn')}
                        />
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default Settings;
